/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import React from "react";
import { useEffectOnce } from "usehooks-ts";
import { getIsWrapOrUnwrap } from "utils/tokenSelectorUtils";
import { TradeDetailsSection } from "../../components/DetailsSection/TradeDetailsSection";
import { YouPaySection } from "../../components/Modal/instance/ConfirmTrade/YouPaySection";
import { YouReceiveSection } from "../../components/Modal/instance/ConfirmTrade/YouReceiveSection";
import { ProgressBar } from "../../components/ProgressBar/ProgressBar";
import { Button } from "../../components/general/Button/Button";
import { Spacer } from "../../components/general/Spacer/Spacer";
import { ReactComponent as ShareTwitterIcon } from "../../img/trade-interface/share_on_twitter.svg";
import tick_icon from "../../img/trade-interface/success.png";
import { GlobalStore, resetOrderState } from "../../store/global/GlobalStore";
import { getAllowances } from "../../store/global/getAllowances";
import { getBalances } from "../../store/global/getBalances";
import { Amount } from "../../types/amount";
import { OrderStatus } from "../../types/orderStatus";
import { getOrderType } from "../../utils/orderTypeUtils";
import { getShareTweetLink } from "../../utils/shareTradeUtils";
import "./OrderCompleted.css";
import { ReviewActionButton } from "./ReviewActionButton";
import { ActionButtonContainer } from "./elements/ActionButtonContainer";

export interface OrderCompletedProps {}

export const OrderCompleted: React.FC<OrderCompletedProps> = () => {
  const orderStatus = GlobalStore.useState(s => s.orderStatus);
  const theme = GlobalStore.useState(s => s.theme);

  const { networkFee } = GlobalStore.useState(s => {
    return { networkFee: s.quote?.usdGasFee };
  });
  const {
    network,
    address,
    quote,
    wrapQuote,
    orderType,
    makerTokens,
    takerTokens,
    tokenMetadata,
    chain,
  } = GlobalStore.useState(s => {
    return {
      network: s.network,
      address: s.address,
      quote: s.quote,
      wrapQuote: s.wrapQuote,
      orderType: getOrderType(s),
      makerTokens: s.makerTokens,
      takerTokens: s.takerTokens,
      tokenMetadata: s.tokens,
      chain: s.network,
    };
  });
  const isWrapOrUnwrap = getIsWrapOrUnwrap(
    takerTokens.map(t => t.token),
    makerTokens.map(t => t.token),
    chain
  );

  useEffectOnce(() => {
    if (tokenMetadata) {
      void getBalances.run({ address: address as `0x${string}`, tokenMetadata, chain: network });
      void getAllowances.run({ address: address as `0x${string}`, tokenMetadata });
    }
  });
  const twitterShareTweet = getShareTweetLink(
    takerTokens.map(token => token.token),
    makerTokens.map(token => token.token)
  );

  return (
    <>
      <div className="review-order">
        <div className="col-12 text-center">
          <h5 className="w-100 text-center m-0 trade">Order Completed</h5>
        </div>
        <div className="sp10"></div>
        <div className="col-12 text-center">
          <img src={tick_icon} width={115} height={116} alt="Order completed" />
          <div className="sp12"></div>
          <p
            className="text-secondary m-0"
            css={css`
              font-size: ${theme.fontSize["5"]};
              font-family: ${theme.fontFamilies["work-sans"]};
            `}
          >
            Your trade was completed successfully!
          </p>
          <div className="sp12"></div>
          <a
            style={{ color: theme.button.tertiary.text.default }}
            target="_blank"
            rel="nofollow noreferrer"
            href={twitterShareTweet}
          >
            <ShareTwitterIcon width={16.5} /> Share Trade
          </a>
        </div>
        <div className="sp12"></div>
        <div>
          <YouPaySection shouldDisplayLabel completed />
          <YouReceiveSection shouldDisplayLabel completed />
        </div>
        {orderStatus === OrderStatus.COMPLETED && !isWrapOrUnwrap && (
          <>
            <Spacer height="22px" />
            <TradeDetailsSection networkFee={networkFee ?? Amount.ZERO} visible={true} />
          </>
        )}
        {orderStatus === OrderStatus.COMPLETED && <Spacer height="10px" />}
        <div className={"progress-bar-container"}>
          <ProgressBar step={4} />
        </div>
        <ActionButtonContainer>
          {(quote != null || wrapQuote != null) && orderType && <ReviewActionButton />}
          <Button
            buttonType="primary"
            text="Make Another Trade"
            size="Large"
            onClick={resetOrderState}
            widthExpand={true}
            buttonCss={css`
              padding: 19px 18px;
            `}
            height={56}
          />
        </ActionButtonContainer>
      </div>
    </>
  );
};
